import { useState, useRef, useEffect } from "react";
import { 
  MdSearch, 
  MdAutoFixHigh, 
  MdStop, 
  MdContentCopy, 
  MdCheck, 
  MdKeyboardArrowDown,
  MdOutlineComputer,
  MdScience,
  MdErrorOutline
} from "react-icons/md";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { apiFetch } from "../../utils/api";
import "./seoReport.css";

// const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:9500/api/v1";

const SEOAnalyzer = ({ restoredData, onAnalysisComplete }) => {
  const [url, setUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [mode, setMode] = useState("live");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [projects, setProjects] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  const abortRef = useRef(null);
  const resultRef = useRef(null);

  useEffect(() => {
    if (restoredData) {
      setUrl(restoredData.url || "");
      setResult(restoredData.result || "");
      setModel(restoredData.model || "claude-sonnet-4-6");
      setError("");
      setStatusMsg("");
    }
    
    const fetchProjects = async () => {
      try {
        const res = await apiFetch("/projects/list");
        if (res.ok) {
          const data = await res.json();
          setProjects(data);
        }
      } catch (err) {
        console.error("Failed to load projects:", err);
      }
    };
    fetchProjects();
  }, [restoredData]);

  const filteredProjects = projects.filter(p => 
    p.domain.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.projectName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectProject = (domain) => {
    const formattedUrl = domain.startsWith("http") ? domain : `https://${domain}`;
    setUrl(formattedUrl);
    setIsDropdownOpen(false);
    setSearchQuery("");
  };

  useEffect(() => {
    if (analyzing && resultRef.current) {
      resultRef.current.scrollTop = resultRef.current.scrollHeight;
    }
  }, [result, analyzing]);

  const handleAnalyze = async () => {
    if (!url.trim()) return;

    setAnalyzing(true);
    setResult("");
    setStatusMsg("");
    setError("");

    const controller = new AbortController();
    abortRef.current = controller;

    let accumulated = "";

    try {
      const response = await apiFetch("/seo/analyze", {
        method: "POST",
        headers: {
          "Accept": "text/event-stream",
        },
        body: JSON.stringify({
          url: url.trim(),
          apiKey: apiKey.trim(),
          model,
          mode
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Request failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === "status") setStatusMsg(event.message);
            else if (event.type === "text") {
              accumulated += event.content;
              setResult(accumulated);
            } else if (event.type === "error") {
              setError(event.message);
              setStatusMsg("");
              setAnalyzing(false);
            } else if (event.type === "done") {
              setStatusMsg("");
              if (onAnalysisComplete) onAnalysisComplete();
            }
          } catch { /* ignore parse errors in fragments */ }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setError(err.message || "Analysis failed");
      }
    } finally {
      setAnalyzing(false);
      abortRef.current = null;
    }
  };

  const stop = () => {
    abortRef.current?.abort();
    setAnalyzing(false);
    setStatusMsg("");
  };

  const copyResult = () => {
    navigator.clipboard.writeText(result).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="seo-analyzer-container">
      <div className="card border-0 shadow-sm p-4 mb-4">
        <div className="row g-3">
          <div className="col-md-7">
            <div className="analyzer-header">
              <div className="input-group">
                <label className="form-label fw-bold small text-uppercase text-muted">Webpage URL</label>
                <div className="url-input-container">
                  <div className="url-input-wrapper d-flex">
                    <span className="input-group-text bg-light border-end-0"><MdSearch /></span>
                    <input 
                      type="text" 
                      className="form-control border-start-0 ps-0" 
                      placeholder="https://example.com"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      disabled={analyzing}
                    />
                  </div>

                  <div className="project-selector mt-2">
                    <button 
                      type="button" 
                      className="btn btn-sm btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    >
                      <span><MdOutlineComputer className="me-1" /> Browse Projects</span>
                      <MdKeyboardArrowDown className={`arrow ${isDropdownOpen ? 'up' : ''}`} />
                    </button>

                    {isDropdownOpen && (
                      <div className="dropdown-panel border shadow-sm mt-1 p-2 bg-white position-absolute w-100" style={{ zIndex: 1000 }}>
                        <div className="dropdown-search mb-2">
                          <input 
                            type="text" 
                            className="form-control form-control-sm"
                            placeholder="Search projects..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoFocus
                          />
                        </div>
                        <div className="dropdown-list" style={{ maxHeight: "200px", overflowY: "auto" }}>
                          {filteredProjects.length > 0 ? (
                            filteredProjects.map((proj) => (
                              <div 
                                key={proj._id} 
                                className="dropdown-item p-2 border-bottom cursor-pointer"
                                onClick={() => handleSelectProject(proj.domain)}
                                style={{ cursor: "pointer" }}
                              >
                                <div className="fw-bold small">{proj.projectName}</div>
                                <div className="text-muted x-small">{proj.domain}</div>
                              </div>
                            ))
                          ) : (
                            <div className="dropdown-no-results p-2 text-muted small">No projects found</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-5">
            <label className="form-label fw-bold small text-uppercase text-muted d-flex justify-content-between">
              Custom API Key <small className="text-info fw-normal">Optional</small>
            </label>
            <input 
              type="password" 
              className="form-control" 
              placeholder="Anthropic sk-..."
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              disabled={analyzing}
            />
          </div>
          
          <div className="col-md-4">
            <label className="form-label fw-bold small text-uppercase text-muted">Analysis Model</label>
            <select className="form-select" value={model} onChange={(e) => setModel(e.target.value)} disabled={analyzing}>
              <option value="claude-sonnet-4-6">Claude Sonnet 4.6 (Recommended)</option>
              <option value="claude-haiku-4-5-20251001">Claude Haiku 4.5 (Fastest)</option>
              <option value="claude-opus-4-6">Claude Opus 4.6 (Deep Analysis)</option>
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label fw-bold small text-uppercase text-muted">Operation Mode</label>
            <div className="btn-group w-100">
              <button 
                className={`btn btn-sm ${mode === 'live' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setMode('live')}
                disabled={analyzing}
              >
                Live Analysis
              </button>
              <button 
                className={`btn btn-sm ${mode === 'mock' ? 'btn-info' : 'btn-outline-info'}`}
                onClick={() => setMode('mock')}
                disabled={analyzing}
                title="Use mock data (No cost)"
              >
                <MdScience className="me-1" /> Sandbox
              </button>
            </div>
          </div>

          <div className="col-md-4 d-flex align-items-end">
            {!analyzing ? (
              <button className="btn btn-primary w-100 py-2 fw-bold" onClick={handleAnalyze}>
                <MdAutoFixHigh className="me-2" /> Start SEO Audit
              </button>
            ) : (
              <button className="btn btn-danger w-100 py-2 fw-bold" onClick={stop}>
                <MdStop className="me-2" /> Stop Analysis
              </button>
            )}
          </div>
        </div>
      </div>

      {statusMsg && (
        <div className="alert alert-info border-0 shadow-sm d-flex align-items-center gap-2 mb-4">
          <div className="spinner-border spinner-border-sm text-info" role="status"></div>
          <span>{statusMsg}</span>
        </div>
      )}

      {error && (
        <div className="alert alert-danger border-0 shadow-sm mb-4">
          <div className="d-flex align-items-center gap-2">
            <MdErrorOutline size={20} className="flex-shrink-0" />
            <span>{error}</span>
          </div>
          {(error.toLowerCase().includes("credit") || error.toLowerCase().includes("billing") || error.toLowerCase().includes("quota")) && !apiKey && (
            <div className="mt-2 small text-danger-emphasis">
              The server API key is out of credits. Enter your own <strong>Custom API Key</strong> above to continue.
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="card border-0 shadow-sm overflow-hidden">
          <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center py-3">
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-primary">Report Result</span>
              {analyzing && <span className="typing-indicator small opacity-75">AI is thinking...</span>}
            </div>
            <button className="btn btn-sm btn-outline-light" onClick={copyResult}>
              {copied ? <MdCheck className="text-success" /> : <MdContentCopy />} {copied ? "Copied!" : "Copy Report"}
            </button>
          </div>
          <div
            ref={resultRef}
            className="card-body bg-light p-4 seo-report"
            style={{ minHeight: "400px", maxHeight: "800px", overflowY: "auto" }}
          >
            {/* react-markdown escapes raw HTML by default — do NOT add rehype-raw.
                The report is AI output derived from a user-supplied URL's page
                content, so any HTML in it must stay inert, never be rendered. */}
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Wide tables scroll inside their own container, never the page
                table: ({ node, ...props }) => (
                  <div className="seo-report-table-wrap">
                    <table {...props} />
                  </div>
                ),
              }}
            >
              {result}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
};

export default SEOAnalyzer;
